import { API_BASE_URL } from '../lib/constants';
import { useAuthStore } from '../stores/authStore';
// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
let refreshPromise = null;
// Refresh the access token using the httpOnly refresh cookie
async function refreshAccessToken() {
    try {
        const response = await fetch(`${API_BASE_URL}/auth/refresh`, {
            method: 'POST',
            credentials: 'include',
        });
        if (response.ok) {
            const data = await response.json();
            if (data.token) {
                if (data.user) {
                    useAuthStore.getState().setAuth(data.user, data.token);
                }
                else {
                    localStorage.setItem('token', data.token);
                }
                return true;
            }
        }
        return false;
    }
    catch {
        return false;
    }
}
// Wrapper to ensure only one refresh happens at a time
export async function tryRefreshToken() {
    if (isRefreshing && refreshPromise) {
        return refreshPromise;
    }
    isRefreshing = true;
    refreshPromise = refreshAccessToken().finally(() => {
        isRefreshing = false;
        refreshPromise = null;
    });
    return refreshPromise;
}
// Force logout and redirect to login
function forceLogout() {
    localStorage.removeItem('token');
    localStorage.removeItem('auth-storage');
    window.location.href = '/login';
}
async function apiRequest(endpoint, options = {}, isRetry = false) {
    const token = localStorage.getItem('token');
    const headers = {
        ...options.headers,
    };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    // Don't set Content-Type for FormData
    if (!(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
    }
    try {
        const response = await fetch(`${API_BASE_URL}${endpoint}`, {
            ...options,
            headers,
            credentials: 'include',
        });
        // Handle 401 Unauthorized - try to refresh token
        if (response.status === 401 && !isRetry && !endpoint.includes('/auth/')) {
            const refreshed = await tryRefreshToken();
            if (refreshed) {
                return apiRequest(endpoint, options, true);
            }
            else {
                forceLogout();
                return { error: 'Session expired. Please log in again.' };
            }
        }
        // A host / gateway error page (e.g. 502/504 HTML) isn't JSON — report the status instead of "network error".
        let data;
        try {
            data = await response.json();
        }
        catch {
            if (response.status >= 502 && response.status <= 504) {
                return { error: `The server timed out or couldn't be reached (HTTP ${response.status}). Please try again.` };
            }
            return { error: `Unexpected server response (HTTP ${response.status}).` };
        }
        if (!response.ok) {
            if (data.errors && Array.isArray(data.errors)) {
                const errorMessages = data.errors
                    .map((err) => err.field ? `${err.field}: ${err.message}` : err.message)
                    .join('\n');
                return { error: errorMessages || data.message || 'Validation error' };
            }
            return { error: data.message || 'An error occurred' };
        }
        return { data };
    }
    catch (error) {
        return { error: 'Network error. Please try again.' };
    }
}
export const api = {
    get: (endpoint) => apiRequest(endpoint, { method: 'GET' }),
    post: (endpoint, body) => apiRequest(endpoint, {
        method: 'POST',
        body: body instanceof FormData ? body : JSON.stringify(body),
    }),
    put: (endpoint, body) => apiRequest(endpoint, {
        method: 'PUT',
        body: JSON.stringify(body),
    }),
    delete: (endpoint) => apiRequest(endpoint, { method: 'DELETE' }),
};
// ─── Auth API ─────────────────────────────────────────
export const authApi = {
    login: (email, password) => api.post('/auth/login', { email, password }),
    forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
    resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
};
// ─── User API ─────────────────────────────────────────
export const userApi = {
    getProfile: () => api.get('/users/me'),
    updateProfile: (data) => api.put('/users/me', data),
    changePassword: (data) => api.put('/users/me/password', data),
};
// ─── Positions API ────────────────────────────────────
export const positionApi = {
    list: (page = 1, limit = 20, status, positionType) => api.get(`/positions?page=${page}&limit=${limit}${status ? `&status=${status}` : ''}${positionType ? `&position_type=${positionType}` : ''}`),
    get: (id) => api.get(`/positions/${id}`),
    create: (data) => api.post('/positions', data),
    update: (id, data) => api.put(`/positions/${id}`, data),
    delete: (id) => api.delete(`/positions/${id}`),
    resolve: (id, data) => api.post(`/positions/${id}/resolve`, data),
    roll: (id, data) => api.post(`/positions/${id}/roll`, data),
    refreshPrices: () => api.post('/positions/refresh-prices'),
};
// ─── Fund API ─────────────────────────────────────────
export const fundApi = {
    getSettings: () => api.get('/fund/settings'),
    updateSettings: (data) => api.put('/fund/settings', data),
    getCapacity: () => api.get('/fund/capacity'),
};
// ─── Admin API ────────────────────────────────────────
export const adminApi = {
    getDashboardStats: () => api.get('/admin/dashboard/stats'),
    getInvestorView: () => api.get('/admin/dashboard/investor-view'),
    // Moomoo
    getMoomooFunds: () => api.get('/admin/moomoo/funds'),
    getMoomooAccounts: () => api.get('/admin/moomoo/accounts'),
    getEarningsReconciliation: () => api.get('/admin/earnings/reconciliation'),
    // Investors
    getInvestors: (page = 1, limit = 20, search) => api.get(`/admin/investors?page=${page}&limit=${limit}${search ? `&search=${encodeURIComponent(search)}` : ''}`),
    getInvestor: (id) => api.get(`/admin/investors/${id}`),
    getInvestorStatement: (id) => api.get(`/admin/investors/${id}/statement`),
    createInvestor: (data) => api.post('/admin/investors', data),
    updateInvestor: (id, data) => api.put(`/admin/investors/${id}`, data),
    deleteInvestor: (id) => api.delete(`/admin/investors/${id}`),
    getFundSummary: () => api.get('/admin/investors/fund-summary'),
    resetInvestorPassword: (id, password) => api.post(`/admin/investors/${id}/reset-password`, { password }),
    addCapital: (id, data) => api.post(`/admin/investors/${id}/capital`, data),
    withdrawCapital: (id, data) => api.post(`/admin/investors/${id}/withdraw`, data),
    getInvestorValue: (id) => api.get(`/admin/investors/${id}/value`),
    getLastCapital: () => api.get('/admin/capital/last'),
    undoLastCapital: () => api.post('/admin/capital/undo-last', {}),
    // P&L
    getPnl: (period = 'all') => api.get(`/admin/pnl?period=${period}`),
    createPnlRecord: (data) => api.post('/admin/pnl', data),
    // Announcements
    getAnnouncements: (page = 1, limit = 20) => api.get(`/admin/announcements?page=${page}&limit=${limit}`),
    createAnnouncement: (data) => api.post('/admin/announcements', data),
    updateAnnouncement: (id, data) => api.put(`/admin/announcements/${id}`, data),
    deleteAnnouncement: (id) => api.delete(`/admin/announcements/${id}`),
    // Risk
    getRiskDashboard: () => api.get('/admin/risk/dashboard'),
    // Audit
    getAuditTrail: (page = 1, limit = 50, action, userId, startDate, endDate) => api.get(`/admin/audit?page=${page}&limit=${limit}${action ? `&action=${action}` : ''}${userId ? `&user_id=${userId}` : ''}${startDate ? `&start_date=${startDate}` : ''}${endDate ? `&end_date=${endDate}` : ''}`),
    // Export
    exportPositions: (status) => {
        const token = localStorage.getItem('token');
        window.open(`${API_BASE_URL}/admin/export/positions?token=${encodeURIComponent(token || '')}${status ? `&status=${status}` : ''}`, '_blank');
    },
};
// ─── Investor API (read-only) ─────────────────────────
export const scannerApi = {
    getWatchlist: () => api.get('/admin/scanner/watchlist'),
    addTicker: (ticker) => api.post('/admin/scanner/watchlist', { ticker }),
    removeTicker: (ticker) => api.delete(`/admin/scanner/watchlist/${ticker}`),
    scan: (params) => api.post('/admin/scanner/scan', params),
    analyze: (results, stock_prices, params) => api.post('/admin/scanner/analyze', { results, stock_prices, params }),
    getLevels: (ticker) => api.get(`/admin/scanner/levels/${encodeURIComponent(ticker)}`),
    // Jev context: tickers = [{ ticker, latest_expiry }]
    context: (tickers) => api.post('/admin/scanner/context', { tickers }),
    getRules: () => api.get('/admin/scanner/rules'),
    addRule: (rule_text, action) => api.post('/admin/scanner/rules', { rule_text, action }),
    updateRule: (id, patch) => api.put(`/admin/scanner/rules/${id}`, patch),
    deleteRule: (id) => api.delete(`/admin/scanner/rules/${id}`),
    jevScores: (ticker, latest_expiry, options) => api.post('/admin/scanner/jev-scores', { ticker, latest_expiry, options }),
    getRollWatch: () => api.get('/admin/scanner/roll-watch'),
    findRolls: (positionId, opts = {}) => api.post(`/admin/scanner/rolls/${positionId}`, opts),
};
export const investorApi = {
    getDashboard: () => api.get('/investor/dashboard'),
    getPositions: (page = 1, limit = 20, status) => api.get(`/investor/positions?page=${page}&limit=${limit}${status ? `&status=${status}` : ''}`),
    getPosition: (id) => api.get(`/investor/positions/${id}`),
    getPnl: (period = 'all') => api.get(`/investor/pnl?period=${period}`),
    getHistory: (page = 1, limit = 20) => api.get(`/investor/history?page=${page}&limit=${limit}`),
    getAnnouncements: () => api.get('/investor/announcements'),
    getNotifications: (page = 1, limit = 20) => api.get(`/investor/notifications?page=${page}&limit=${limit}`),
    markNotificationRead: (id) => api.put(`/investor/notifications/${id}/read`),
    markAllNotificationsRead: () => api.put('/investor/notifications/read-all'),
};
export const notificationApi = {
    preferences: () => api.get('/notifications/preferences'),
    savePreferences: (data) => api.put('/notifications/preferences', data),
    history: () => api.get('/notifications'),
    test: () => api.post('/notifications/test', {}),
    summary: () => api.post('/notifications/summary', {}),
    markRead: (id) => api.put(`/notifications/${id}/read`, {}),
};
// Paper-trading portfolios (admin only)
export const simApi = {
    // live=true marks open positions at the live Moomoo mid before returning
    listPortfolios: (live = false) => api.get(`/sim/portfolios${live ? '?live=1' : ''}`),
    getPortfolio: (id, live = false) => api.get(`/sim/portfolios/${id}${live ? '?live=1' : ''}`),
    createPortfolio: (data) => api.post('/sim/portfolios', data),
    updatePortfolio: (id, data) => api.put(`/sim/portfolios/${id}`, data),
    deletePortfolio: (id) => api.delete(`/sim/portfolios/${id}`),
    quote: (data) => api.post('/sim/quote', data),
    openPut: (portfolioId, data) => api.post(`/sim/portfolios/${portfolioId}/positions`, data),
    closePosition: (id, data = {}) => api.post(`/sim/positions/${id}/close`, data),
    rollPosition: (id, data) => api.post(`/sim/positions/${id}/roll`, data),
    refresh: () => api.post('/sim/refresh', {}),
    quoteSpread: (data) => api.post('/sim/quote-spread', data),
    openSpreads: (portfolioId, data) => api.post(`/sim/portfolios/${portfolioId}/spreads`, data),
    checkExits: () => api.post('/sim/check-exits', {}),
    // Automatic strategy portfolios: rules + decision log + backtest over the same days; Run now; run time
    strategy: (id) => api.get(`/sim/portfolios/${id}/strategy`),
    runStrategy: (id) => api.post(`/sim/portfolios/${id}/strategy/run`, {}),
    updateStrategy: (id, data) => api.put(`/sim/portfolios/${id}/strategy`, data),
};
// Option Alerts — newly listed puts in a DTE window (admin only)
export const optionAlertApi = {
    list: () => api.get('/option-alerts'),
    get: (id) => api.get(`/option-alerts/${id}`),
    create: (data) => api.post('/option-alerts', data),
    update: (id, data) => api.put(`/option-alerts/${id}`, data),
    delete: (id) => api.delete(`/option-alerts/${id}`),
    check: (id) => api.post(`/option-alerts/${id}/check`, {}),
    setDismissed: (contractId, dismissed) => api.put(`/option-alerts/contracts/${contractId}/dismiss`, { dismissed }),
};
// 0DTE credit-spread scan (admin only)
export const zeroDteApi = {
    scan: (data) => api.post('/zero-dte/scan', data),
};
// Backtesting long-dated cash-secured puts (admin only)
export const backtestApi = {
    dataStatus: (ticker) => api.get(`/backtest/data/${ticker}`),
    loadHistory: (ticker) => api.post(`/backtest/data/${ticker}/load`, {}),
    recordToday: (ticker) => api.post(`/backtest/data/${ticker}/record`, {}),
    run: (data) => api.post('/backtest/run', data),
    listRuns: () => api.get('/backtest/runs'),
    getRun: (id) => api.get(`/backtest/runs/${id}`),
    deleteRun: (id) => api.delete(`/backtest/runs/${id}`),
    loadAlpaca: (ticker) => api.post(`/backtest/data/${ticker}/alpaca`, {}),
    listPresets: () => api.get('/backtest/presets'),
    savePreset: (data) => api.post('/backtest/presets', data),
    deletePreset: (id) => api.delete(`/backtest/presets/${id}`),
};
